import React, { useState, useEffect } from 'react';
import { RefreshCw, Play as PlayIcon, Square, SkipBack, Search, Shield, ChevronLeft, ChevronRight, Brain, AlertTriangle, RotateCcw, FlipHorizontal, BookOpen, Lightbulb, SkipForward, BarChart2, Star, Target, MessageSquare, Sparkles, Settings, MousePointer2, Terminal } from 'lucide-react';
import ChessBoard from '../components/ChessBoard';
import CoachPanel from '../components/CoachPanel';
import { useAnalysisLogic } from '../modules/analysis/useAnalysisLogic';
import { useAIFeedback } from '../hooks/useAIFeedback';
import AISettingsModal from '../components/AISettingsModal';
import { MoveCategory, levelToElo } from '@chess-platform/shared';

const getCategoryColor = (cat: MoveCategory | undefined): string => {
    switch (cat) {
        case 'brilliant': return '#a78bfa';
        case 'great': return '#34d399';
        case 'best': return '#22c55e';
        case 'excellent': return '#4ade80';
        case 'good': return '#93c5fd';
        case 'inaccuracy': return '#fbbf24';
        case 'mistake': return '#f97316';
        case 'blunder': return '#ef4444';
        case 'forced': return '#94a3b8';
        case 'book': return '#60a5fa';
        default: return '#cbd5e1';
    }
};

const getCategoryLabel = (cat: MoveCategory | undefined): string => {
    switch (cat) {
        case 'brilliant': return '!!';
        case 'great': return '!';
        case 'best': return '★';
        case 'inaccuracy': return '?!';
        case 'mistake': return '?';
        case 'blunder': return '??';
        case 'book': return '📖';
        default: return '';
    }
};

const AnalysisPage: React.FC = () => {
    const [orientation, setOrientation] = useState<'white' | 'black'>('white');
    const [fenInput, setFenInput] = useState('');
    const [fenError, setFenError] = useState('');
    const [showBestMove, setShowBestMove] = useState(false);
    const [coachExplanation, setCoachExplanation] = useState<any | null>(null);
    const [aiProvider, setAiProvider] = useState<string>('openai');
    const [aiApiKey, setAiApiKey] = useState<string>('');
    const [showAISettings, setShowAISettings] = useState(false);
    const [showMobileAnalysis, setShowMobileAnalysis] = useState(false);

    const { getExplanation, loading: coachLoading } = useAIFeedback();

    const {
        currentFen,
        history,
        historyIndex,
        isEngineOn,
        isEngineReady,
        evaluation,
        currentOpening,
        openingName,
        showComments,
        setShowComments,
        showTraps,
        setShowTraps,
        analysisElo,
        setAnalysisElo,
        makeMove,
        goBack,
        goForward,
        resetBoard,
        loadFen,
        toggleEngine,
        setHistoryIndex,
        isDeepAnalysis,
        toggleDeepAnalysis,
        runDeepAnalysis,
        deepEval,
        deepLoading,
        currentEvidence,
        evidenceArrows,
        evidenceHighlights,
    } = useAnalysisLogic();

    useEffect(() => {
        const savedProvider = localStorage.getItem('chess_ai_provider');
        const savedKey = localStorage.getItem('chess_ai_key');
        if (savedProvider) setAiProvider(savedProvider);
        if (savedKey) setAiApiKey(savedKey);
    }, []);

    const saveAISettings = (provider: string, key: string) => {
        setAiProvider(provider);
        setAiApiKey(key);
        localStorage.setItem('chess_ai_provider', provider);
        localStorage.setItem('chess_ai_key', key);
        setShowAISettings(false);
    };

    useEffect(() => {
        setCoachExplanation(null);
    }, [historyIndex]);

    const handleAskCoach = async () => {
        if (!currentEvidence) return;

        const explanation = await getExplanation({
            evidence: currentEvidence,
            provider: aiProvider,
            apiKey: aiApiKey || undefined
        });

        if (explanation) setCoachExplanation(explanation);
    };

    const liveArrows: [string, string, string][] = showBestMove && evaluation.bestMove && !currentEvidence ? [
        [evaluation.bestMove.slice(0, 2), evaluation.bestMove.slice(2, 4), 'rgba(34, 197, 94, 0.7)']
    ] : [];

    const trapArrows: [string, string, string][] = [];
    if (showTraps && history[historyIndex].comment?.heuristics) {
        history[historyIndex].comment?.heuristics.forEach((h: any) => {
            if (h.id === 'hangs_piece' && h.data?.square) {
                trapArrows.push([h.data.square, h.data.square, 'rgba(239, 68, 68, 0.8)']);
            }
        });
    }

    const arrows: [string, string, string][] = showComments && currentEvidence
        ? [...evidenceArrows, ...trapArrows]
        : [...liveArrows, ...trapArrows];

    const handleLoadFen = () => {
        const ok = loadFen(fenInput);
        if (!ok) setFenError('FEN inválido');
        else setFenError('');
    };

    return (
        <div className="flex flex-col lg:flex-row gap-4 h-[100dvh] lg:h-[100dvh] p-2 lg:p-8 animate-in fade-in duration-500 overflow-hidden bg-[#0a0c10] overscroll-none text-white selection:bg-indigo-500/30">
            {/* LEFT: Board area */}
            <div className="flex-[3] flex flex-col gap-2 lg:gap-4 min-h-0 relative">
                {/* Status Bar */}
                <div className="flex items-center justify-between px-2 py-1 lg:py-0">
                    <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1.5 px-2 lg:px-3 py-1 lg:py-1.5 rounded-full border text-[8px] lg:text-[10px] font-black uppercase tracking-wider transition-colors ${!isEngineReady
                            ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                            : (isEngineOn)
                                ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400'
                                : 'bg-white/5 border-white/5 text-white/30'
                            }`}>
                            <div className={`w-1 h-1 lg:w-1.5 lg:h-1.5 rounded-full ${!isEngineReady
                                ? 'bg-amber-400 animate-pulse'
                                : isEngineOn
                                    ? 'bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.5)] animate-pulse'
                                    : 'bg-white/20'
                                }`} />
                            <span>{!isEngineReady ? 'IA...' : (isEngineOn ? 'Analizando' : 'IA Lista')}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-1 lg:gap-1.5 bg-white/5 p-0.5 lg:p-1 rounded-lg lg:rounded-xl border border-white/5 shadow-2xl overflow-x-auto max-w-[60vw]">
                        <button onClick={() => setShowTraps(!showTraps)} className={`p-1.5 lg:p-2 rounded-lg transition-all ${showTraps ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'text-white/20 hover:text-white/40'}`}>
                            <AlertTriangle size={12} className="lg:w-[14px] lg:h-[14px]" />
                        </button>
                        <button onClick={() => setShowComments(!showComments)} className={`p-1.5 lg:p-2 rounded-lg transition-all ${showComments ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'text-white/20 hover:text-white/40'}`}>
                            <MessageSquare size={12} className="lg:w-[14px] lg:h-[14px]" />
                        </button>
                        <button onClick={toggleDeepAnalysis} className={`p-1.5 lg:p-2 rounded-lg transition-all ${isDeepAnalysis ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'text-white/20 hover:text-white/40'}`}>
                            <Search size={12} className="lg:w-[14px] lg:h-[14px]" />
                        </button>
                        <button onClick={() => setOrientation(o => o === 'white' ? 'black' : 'white')} className="p-1.5 lg:p-2 rounded-lg text-white/20 hover:text-white/40 transition-all"><FlipHorizontal size={12} /></button>
                        <button onClick={() => setShowBestMove(!showBestMove)} className={`p-1.5 lg:p-2 rounded-lg transition-all ${showBestMove ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'text-white/20 hover:text-white/40'}`}><Lightbulb size={12} /></button>
                    </div>
                </div>

                <div className="flex-1 flex flex-col relative min-h-0">
                    <div className="flex-1 flex items-center justify-center p-2 lg:p-4 overflow-hidden">
                        <div className="w-full h-full max-w-[85vh] aspect-square relative flex gap-2 lg:gap-4">
                            {/* Eval Bar */}
                            <div className="w-2 lg:w-4 bg-[#1e293b] rounded-full overflow-hidden flex flex-col-reverse border border-white/10 relative shrink-0">
                                <div
                                    className="bg-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.5)] transition-all duration-700 ease-out"
                                    style={{ height: evaluation.score !== null ? `${Math.min(Math.max(50 + (evaluation.score * 5), 5), 95)}%` : '50%' }}
                                />
                                <div className="absolute top-1/2 w-full h-[1px] bg-white/20 z-10" />
                            </div>

                            <div className="flex-1 relative">
                                <ChessBoard
                                    fen={currentFen}
                                    onMove={(move) => makeMove(move.from, move.to)}
                                    onSquareClick={isDeepAnalysis ? (square: string) => runDeepAnalysis(square) : undefined}
                                    orientation={orientation}
                                    draggable={!isDeepAnalysis}
                                    customArrows={arrows}
                                    customSquareStyles={showComments ? evidenceHighlights : {}}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* CONTROLES DE NAVEGACION (EL "TECLADO" UI) */}
                <div className="flex flex-col gap-2 py-2 shrink-0 px-4">
                    <div className="flex items-center justify-center gap-2 lg:gap-4">
                        {/* Summary Alerts to the sides of the navigation controls */}
                        <div className="hidden xl:flex items-center gap-2 pr-4 border-r border-white/5 opacity-40 hover:opacity-100 transition-opacity">
                            {showComments && <MessageSquare size={14} className="text-indigo-400" />}
                            {showTraps && <AlertTriangle size={14} className="text-red-400" />}
                        </div>

                        <button onClick={resetBoard} className="p-2.5 rounded-xl bg-white/5 border border-white/5 text-white/20 hover:bg-white/10 hover:text-white transition-all"><RotateCcw size={16} /></button>

                        <div className="flex items-center bg-white/5 rounded-xl border border-white/5 p-0.5 gap-0.5">
                            <button onClick={goBack} disabled={historyIndex === 0} className="p-2.5 rounded-lg hover:bg-white/5 disabled:opacity-10 text-white/60"><ChevronLeft size={18} /></button>
                            <div className="px-3 text-[9px] font-black tabular-nums text-white/20">{historyIndex} / {history.length - 1}</div>
                            <button onClick={goForward} disabled={historyIndex === history.length - 1} className="p-2.5 rounded-lg hover:bg-white/5 disabled:opacity-10 text-white/60"><ChevronRight size={18} /></button>
                        </div>

                        <button
                            onClick={toggleEngine}
                            className={`px-4 py-2.5 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all flex items-center gap-2 border ${isEngineOn ? 'bg-indigo-500 text-white border-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.2)]' : 'bg-white/5 text-white/20 border-white/10'}`}
                        >
                            {isEngineOn ? <Square size={10} fill="currentColor" /> : <PlayIcon size={10} fill="currentColor" />}
                            <span className="hidden sm:inline">{isEngineOn ? 'Parar' : 'Analizar'}</span>
                        </button>

                        <div className="hidden xl:flex items-center gap-2 pl-4 border-l border-white/5 opacity-40 hover:opacity-100 transition-opacity">
                            <span className="text-[10px] uppercase font-black tracking-widest text-white/20">Modo Análisis</span>
                        </div>

                        {/* MOBILE FEED TOGGLE */}
                        <button
                            onClick={() => setShowMobileAnalysis(!showMobileAnalysis)}
                            className={`lg:hidden p-2.5 rounded-xl border transition-all ${showMobileAnalysis ? 'bg-white text-black border-white' : 'bg-white/5 text-white/20 border-white/10'}`}
                        >
                            <BarChart2 size={16} />
                        </button>
                    </div>

                    {/* Opening Info near navigation for easy reading */}
                    {openingName && (
                        <div className="text-center">
                            <span className="text-[9px] font-black text-indigo-400/60 uppercase tracking-[0.2em] animate-in fade-in duration-300">
                                {openingName}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* RIGHT: Analysis Feed Drawer - Balanced */}
            <div className={`
                flex-1 flex flex-col gap-3 lg:gap-4 w-full lg:w-[380px] 
                fixed lg:relative inset-x-0 bottom-0 z-50 lg:z-auto bg-[#0a0c10] lg:bg-transparent
                transition-transform duration-500 ease-[cubic-bezier(0.16, 1, 0.3, 1)] 
                ${showMobileAnalysis ? 'translate-y-0 h-[75vh] lg:h-full' : 'translate-y-full lg:translate-y-0 h-0 lg:h-full'} 
                lg:flex overflow-hidden shrink-0 border-t lg:border-t-0 border-white/10 shadow-[0_-20px_50px_rgba(0,0,0,0.8)] lg:shadow-none
                lg:p-4
            `}>
                {/* Drag Handle */}
                <div className="lg:hidden flex justify-center py-3 shrink-0" onClick={() => setShowMobileAnalysis(false)}>
                    <div className="w-12 h-1.5 bg-white/10 rounded-full" />
                </div>

                <div className="flex-1 overflow-y-auto p-4 lg:p-0 flex flex-col gap-4 custom-scrollbar">
                    {/* MAESTRO VIRTUAL PANEL */}
                    {showComments && (
                        <div className="shrink-0">
                            {currentEvidence ? (
                                <CoachPanel
                                    evidence={currentEvidence}
                                    onAskCoach={handleAskCoach}
                                    coachExplanation={coachExplanation}
                                    coachLoading={coachLoading}
                                />
                            ) : (
                                <div className="bg-[#1a1c24]/50 rounded-2xl border border-indigo-500/5 p-4 flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400">
                                        <Sparkles size={16} />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-0.5">Coach IA</h4>
                                        <p className="text-[9px] text-white/30 italic leading-snug">
                                            Activa la IA para recibir consejos tácticos.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* OPENING INFO - Variations and Plan */}
                    {(currentOpening as any) && (
                        <div className="bg-[#1a1c24]/50 rounded-2xl border border-white/5 p-4 shadow-xl shrink-0">
                            <div className="flex items-center gap-2 mb-2">
                                <BookOpen size={12} className="text-blue-400" />
                                <h4 className="text-[9px] font-black text-white/30 uppercase tracking-[0.2em]">Teoría de Apertura</h4>
                            </div>
                            <div className="flex flex-col gap-2">
                                <div className="text-[11px] font-black text-blue-400 uppercase">
                                    {(currentOpening as any).name}
                                </div>
                                {(currentOpening as any).commentary && (
                                    <div className="text-[10px] text-white/40 leading-relaxed italic border-l border-white/10 pl-3">
                                        {(currentOpening as any).commentary}
                                    </div>
                                )}
                                {(currentOpening as any).moves && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {(currentOpening as any).moves.slice(0, 8).map((m: string, mi: number) => (
                                            <span key={mi} className="text-[8px] bg-white/5 px-1.5 py-0.5 rounded text-white/20 font-mono">
                                                {m}
                                            </span>
                                        ))}
                                        {(currentOpening as any).moves.length > 8 && <span className="text-[8px] text-white/10 italic">...</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* EVALUATION VIEW - Smaller & Borderless */}
                    <div className="bg-[#1a1c24]/80 rounded-2xl border-none p-4 shadow-xl shrink-0">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-[8px] font-black text-white/20 flex items-center gap-1.5 tracking-[0.15em] uppercase">
                                <Terminal size={12} className="text-zinc-500" /> Motor Chessdrez
                            </h2>
                            {isEngineOn && (
                                <span className="text-[8px] font-black text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded-md">
                                    ELO {levelToElo(analysisElo)}
                                </span>
                            )}
                        </div>

                        {isEngineOn ? (
                            <div className="flex flex-col gap-4">
                                <div className={`text-4xl font-black text-center tabular-nums ${(evaluation.score || 0) >= 0 ? 'text-indigo-400' : 'text-red-400'}`}>
                                    {evaluation.isMate ? `#${evaluation.mateMoves}` : ((evaluation.score || 0) > 0 ? '+' : '') + (evaluation.score?.toFixed(1) || '0.0')}
                                </div>
                                <div className="grid grid-cols-1 gap-1.5">
                                    {evaluation.multipv.slice(0, 3).map((line: any, i: number) => (
                                        <button
                                            key={i}
                                            onClick={() => line.move && makeMove(line.move.slice(0, 2), line.move.slice(2, 4))}
                                            className="w-full flex justify-between items-center p-3 rounded-xl bg-white/2 border border-white/5 hover:bg-white/5 hover:border-indigo-500/30 transition-all group"
                                        >
                                            <span className="text-[11px] font-black uppercase text-white/60 group-hover:text-white tabular-nums font-mono">{line.move}</span>
                                            <span className={`text-[10px] font-bold ${line.score !== null && line.score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                {line.isMate ? `#${line.mateMoves}` : ((line.score || 0) > 0 ? '+' : '') + line.score?.toFixed(1)}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="py-8 text-center border border-dashed border-white/5 rounded-2xl">
                                <p className="text-[10px] text-white/20 italic">Motor en pausa</p>
                            </div>
                        )}
                    </div>

                    {/* HISTORY GRID - Smaller Font & Borderless */}
                    <div className="bg-[#1a1c24]/30 rounded-2xl border-none p-4 flex-1 flex flex-col gap-3 shadow-xl min-h-0 overflow-hidden">
                        <div className="flex items-center justify-between">
                            <h4 className="text-[10px] font-black text-white/30 tracking-widest uppercase">Historial de Galería</h4>
                        </div>
                        <div className="flex-1 overflow-y-auto pr-1 flex flex-wrap gap-2 content-start custom-scrollbar">
                            {history.map((h, i) => {
                                const category = h.evidence?.quality.label || h.comment?.category;
                                const qColor = getCategoryColor(category);
                                const qLabel = getCategoryLabel(category);
                                return (
                                    <button
                                        key={i}
                                        onClick={() => setHistoryIndex(i)}
                                        className={`flex items-center gap-1.5 py-1.5 px-3 rounded-xl transition-all border ${historyIndex === i
                                            ? 'bg-indigo-500 border-indigo-400 text-white font-black shadow-lg shadow-indigo-500/20'
                                            : 'bg-white/5 border-white/5 text-white/40 hover:bg-white/10 hover:border-white/10'}`}
                                    >
                                        <span className="text-[8px] opacity-20 tabular-nums font-mono">{Math.floor(i / 2) + 1}{i % 2 === 0 ? '' : '...'}</span>
                                        <span className="text-[10px] uppercase font-bold tracking-tight">{h.san || (i === 0 ? 'INI' : '...')}</span>
                                        {qLabel && (
                                            <span
                                                className="text-[10px] font-black drop-shadow-[0_0_3px_rgba(0,0,0,0.5)]"
                                                style={{ color: historyIndex === i ? 'white' : qColor }}
                                            >
                                                {qLabel}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* FEN IMPORT - Compact */}
                    <div className="bg-[#1a1c24] rounded-3xl border border-white/5 p-3 flex gap-2">
                        <input
                            type="text"
                            placeholder="Importar FEN..."
                            value={fenInput}
                            onChange={(e) => setFenInput(e.target.value)}
                            className="flex-1 text-[9px] px-4 py-2.5 rounded-xl bg-black/40 border border-white/5 focus:border-indigo-500/30 outline-none transition-all font-mono text-white/60"
                        />
                        <button onClick={handleLoadFen} className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 hover:bg-indigo-500/20 transition-all"><MousePointer2 size={14} /></button>
                    </div>
                </div>
            </div>

            <AISettingsModal
                isOpen={showAISettings}
                onClose={() => setShowAISettings(false)}
                provider={aiProvider}
                apiKey={aiApiKey}
                onSave={saveAISettings}
                setProvider={setAiProvider}
                setApiKey={setAiApiKey}
            />
        </div>
    );
};

export default AnalysisPage;
